/**
 * Apollo Client compatibility wrappers that provide a React Query-compatible API surface.
 *
 * This allows page components to continue using `.mutate()`, `.mutateAsync()`,
 * `.isPending`, and `.isLoading` patterns from the original React Query implementation
 * while the underlying hooks use Apollo Client.
 *
 * In mock auth mode (local dev without backend), queries return mock data
 * instead of making GraphQL calls that would hang indefinitely.
 *
 * This is a temporary migration bridge. Pages should be incrementally updated
 * to use native Apollo patterns directly.
 */
// @ts-nocheck

import { useQuery as useApolloQuery, useMutation as useApolloMutation } from '@apollo/client/react';
import { type DocumentNode, type QueryOptions, type MutationOptions, type OperationVariables, type TypedDocumentNode,  } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import { MOCK_DATA_MAP, MOCK_SINGLETON_MAP } from '../data/mockData';

/**
 * Check if mock data should be used at runtime.
 * This is evaluated dynamically so it can detect window.__ENV__ that's set after module load.
 */
function shouldUseMockData(): boolean {
  // Only use mock data in development
  if (!import.meta.env.DEV) {
    return false;
  }

  try {
    if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).__ENV__) {
      const env = (window as unknown as Record<string, unknown>).__ENV__ as Record<string, string>;
      return env.ENABLE_MOCK_AUTH === 'true';
    }
  } catch { /* fall through */ }

  return false;
}

/**
 * Try to extract an entity type name from a GraphQL DocumentNode.
 * Looks at the first field selection in the first query operation.
 */
function extractQueryName(query: DocumentNode): string | null {
  try {
    for (const def of query.definitions) {
      if (def.kind === 'OperationDefinition' && def.operation === 'query') {
        const firstField = def.selectionSet.selections[0];
        if (firstField && 'name' in firstField) {
          return firstField.name.value;
        }
      }
    }
  } catch {
    // Fall through
  }
  return null;
}

/**
 * Map common query field names to mock data keys.
 */
const QUERY_NAME_TO_ENTITY: Record<string, string> = {
  employees: 'Employee',
  employee: 'Employee',
  payrollRuns: 'PayrollRun',
  payrollRun: 'PayrollRun',
  payPeriods: 'PayPeriod',
  payStubs: 'PayStub',
  payStub: 'PayStub',
  payStubDetail: 'PayStub',
  amendments: 'Amendment',
  timesheets: 'Timesheet',
  timesheet: 'Timesheet',
  whosIn: 'Employee',
  exceptions: 'Exception',
  schedules: 'Schedule',
  schedule: 'Schedule',
  scheduleTemplates: 'ScheduleTemplate',
  leaveBalances: 'LeaveBalance',
  leaveTransactions: 'LeaveTransaction',
  teamLeaveSummary: 'LeaveBalance',
  requests: 'Request',
  request: 'Request',
  policies: 'Policy',
  policy: 'Policy',
  accrualPolicies: 'AccrualPolicy',
  company: 'Company',
  branches: 'Branch',
  departments: 'Department',
  reviews: 'Review',
  review: 'Review',
  reviewDetail: 'Review',
  qualifications: 'Qualification',
  kpis: 'Kpi',
  reports: 'Report',
  reportResult: 'Report',
  documents: 'Document',
  document: 'Document',
  documentFolders: 'Document',
  documentVersions: 'Document',
  documentAcknowledgments: 'Document',
  geoFences: 'GeoFence',
  geoFence: 'GeoFence',
  geoFenceViolations: 'GeoFenceViolation',
  jobs: 'Job',
  job: 'Job',
  jobCostEntries: 'Job',
  jobBudgetSummary: 'Job',
  jobCostReport: 'Job',
  invoices: 'Invoice',
  invoice: 'Invoice',
  invoicePayments: 'Invoice',
  invoiceSummary: 'Invoice',
  expenses: 'Expense',
  expense: 'Expense',
  expenseReports: 'ExpenseReport',
  expenseReport: 'ExpenseReport',
  jobPostings: 'JobPosting',
  jobPosting: 'JobPosting',
  applicants: 'Applicant',
  applicant: 'Applicant',
  interviews: 'Interview',
  onboardingChecklists: 'OnboardingChecklist',
  onboardingChecklist: 'OnboardingChecklist',
  bankAccounts: 'BankAccount',
  bankAccountHistory: 'BankAccount',
  recurringSchedules: 'RecurringSchedule',
  auditLogs: 'AuditLog',
  importJobs: 'ImportJob',
  exportJobs: 'ExportJob',
  timesheetVerifications: 'TimesheetVerification',
  enhancedPunches: 'Timesheet',
  payrollPunchRecords: 'Timesheet',
  payrollTimeEntries: 'Timesheet',
  payrollTimesheets: 'Timesheet',
  payrollWhosIn: 'Timesheet',
  punchPolicy: 'Policy',
  employeeLocations: 'Employee',
};

/**
 * Query name to extraction path mapping.
 * Each query field name maps to how the data should be structured in the response.
 */
const QUERY_TO_RESPONSE_STRUCTURE: Record<string, (items: unknown[]) => unknown> = {
  // Most queries use: { [queryName]: { data: [...] } }
  employees: (items) => ({ employees: { data: items } }),
  payrollRuns: (items) => ({ payrollRuns: { data: items } }),
  payPeriods: (items) => ({ payPeriods: { data: items } }),
  timesheets: (items) => ({ timesheets: { data: items } }),
  amendments: (items) => ({ amendments: { data: items } }),
  timesheetVerifications: (items) => ({ timesheetVerifications: { data: items } }),
  bankAccounts: (items) => ({ bankAccounts: { data: items } }),
  accrualPolicies: (items) => ({ accrualPolicies: { data: items } }),
  policies: (items) => ({ policies: { data: items } }),
  importJobs: (items) => ({ importJobs: { data: items } }),
  recurringSchedules: (items) => ({ recurringSchedules: { data: items } }),
  documents: (items) => ({ documents: { data: items } }),
  geoFences: (items) => ({ geoFences: { data: items } }),
  invoices: (items) => ({ invoices: { data: items } }),
  expenseReports: (items) => ({ expenseReports: { data: items } }),
  reviews: (items) => ({ reviews: { data: items } }),
  requests: (items) => ({ requests: { data: items } }),
  jobs: (items) => ({ jobs: { data: items } }),
  jobPostings: (items) => ({ jobPostings: { data: items } }),
  auditLog: (items) => ({ auditLog: { data: items } }),

  // Special patterns (no .data wrapper)
  scheduleWeek: (items) => ({ scheduleWeek: { shifts: items } }),
  kpis: (items) => ({ kpis: items }),
  leaveBalances: (items) => ({ leaveBalances: items }),
  qualifications: (items) => ({ qualifications: items }),
  enhancedPunches: (items) => ({ enhancedPunches: items }),
  payrollPunchRecords: (items) => ({ payrollPunchRecords: items }),
  payrollTimeEntries: (items) => ({ payrollTimeEntries: items }),
  payrollTimesheets: (items) => ({ payrollTimesheets: items }),
  payrollWhosIn: (items) => ({ payrollWhosIn: items }),
  savedReports: (items) => ({ savedReports: items }),
};

/**
 * Get mock data for a query by inspecting the GraphQL document.
 * Wraps data to match repository extractList expectations.
 */
function getMockDataForQuery(query: DocumentNode): unknown {
  const queryName = extractQueryName(query);
  if (!queryName) return undefined;

  const entityKey = QUERY_NAME_TO_ENTITY[queryName];
  if (!entityKey) return undefined;

  // Check singleton map first (e.g., company)
  if (MOCK_SINGLETON_MAP[entityKey]) {
    return { [queryName]: MOCK_SINGLETON_MAP[entityKey] };
  }

  // Get mock items array
  const items = MOCK_DATA_MAP[entityKey] || [];

  // Use specific response structure formatter if available, otherwise wrap with { data: [...] }
  const formatter = QUERY_TO_RESPONSE_STRUCTURE[queryName];
  if (formatter) {
    return formatter(items);
  }

  // Default: wrap in { data: [...] } format
  return { [queryName]: { data: items } };
}

// ── Query Compat ──────────────────────────────────────────────────

interface CompatQueryResult<TData> {
  data: TData | undefined;
  isLoading: boolean;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
}

export function useCompatQuery<TData = unknown, TVars extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVars>,
  options?: QueryOptions<TData, TVars>,
): CompatQueryResult<TData> {
  // In mock mode, skip Apollo and return mock data directly
  if (shouldUseMockData()) {
    const mockData = useMemo(() => getMockDataForQuery(query as DocumentNode) as TData | undefined, [query]);
    return useMemo(
      () => ({
        data: mockData,
        isLoading: false,
        loading: false,
        error: undefined,
        refetch: () => { /* no-op in mock mode */ },
        startPolling: () => { /* no-op */ },
        stopPolling: () => { /* no-op */ },
      }),
      [mockData],
    );
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const result = useApolloQuery<TData, TVars>(query, options);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMemo(
    () => ({
      data: result.data,
      isLoading: result.loading,
      loading: result.loading,
      error: result.error,
      refetch: result.refetch,
      startPolling: result.startPolling,
      stopPolling: result.stopPolling,
    }),
    [result.data, result.loading, result.error, result.refetch, result.startPolling, result.stopPolling],
  );
}

// ── Mutation Compat ───────────────────────────────────────────────

interface CompatMutationResult<TData, TVars> {
  mutate: (variables?: TVars, callbacks?: { onSuccess?: (data: TData) => void; onError?: (error: Error) => void }) => void;
  mutateAsync: (variables?: TVars) => Promise<TData>;
  isPending: boolean;
  loading: boolean;
  data: TData | undefined | null;
  error: Error | undefined;
  reset: () => void;
}

export function useCompatMutation<TData = unknown, TVars extends OperationVariables = OperationVariables>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVars>,
  options?: MutationOptions<TData, TVars>,
): CompatMutationResult<TData, TVars> {
  // In mock mode, mutations are no-ops that return success
  if (shouldUseMockData()) {
    const [mockData, setMockData] = useState<TData | null>(null);
    const [mockLoading, setMockLoading] = useState(false);

    const mutate = useCallback(
      (variables?: TVars, callbacks?: { onSuccess?: (data: TData) => void; onError?: (error: Error) => void }) => {
        setMockLoading(true);
        setTimeout(() => {
          const result = (variables || {}) as unknown as TData;
          setMockData(result);
          setMockLoading(false);
          callbacks?.onSuccess?.(result);
        }, 300);
      },
      [],
    );

    const mutateAsync = useCallback(
      async (variables?: TVars): Promise<TData> => {
        setMockLoading(true);
        await new Promise((r) => setTimeout(r, 300));
        const result = (variables || {}) as unknown as TData;
        setMockData(result);
        setMockLoading(false);
        return result;
      },
      [],
    );

    return useMemo(
      () => ({
        mutate,
        mutateAsync,
        isPending: mockLoading,
        loading: mockLoading,
        data: mockData,
        error: undefined,
        reset: () => setMockData(null),
      }),
      [mutate, mutateAsync, mockLoading, mockData],
    );
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [executeMutation, result] = useApolloMutation<TData, TVars>(mutation, options);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const mutate = useCallback(
    (variables?: TVars, callbacks?: { onSuccess?: (data: TData) => void; onError?: (error: Error) => void }) => {
      executeMutation({ variables: variables as TVars })
        .then((res) => {
          if (res.data && callbacks?.onSuccess) {
            callbacks.onSuccess(res.data);
          }
        })
        .catch((err: any) => {
          if (callbacks?.onError) {
            callbacks.onError(err);
          }
        });
    },
    [executeMutation],
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const mutateAsync = useCallback(
    async (variables?: TVars): Promise<TData> => {
      const res = await executeMutation({ variables: variables as TVars });
      return res.data as TData;
    },
    [executeMutation],
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMemo(
    () => ({
      mutate,
      mutateAsync,
      isPending: result.loading,
      loading: result.loading,
      data: result.data,
      error: result.error,
      reset: result.reset,
    }),
    [mutate, mutateAsync, result.loading, result.data, result.error, result.reset],
  );
}
